import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Mail, ArrowLeft, Loader2, CheckCircle2 } from "lucide-react";

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { error: toastError } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      toastError("Validation Error", "Please enter your email address.");
      return;
    }

    setSubmitting(true);
    try {
      await api.post("/api/auth/forgot-password", { email: email.trim() });
      setSubmitted(true);
    } catch (err: any) {
      toastError("Request Failed", err.message || "Unable to send password reset email.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center p-4 bg-slate-100">
      <Card className="w-full max-w-md shadow-xl bg-white border-slate-200">
        <CardHeader className="space-y-1">
          <Link
            to="/login"
            className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground mb-2 transition-colors"
          >
            <ArrowLeft className="size-3.5" />
            Back to login
          </Link>
          <CardTitle className="text-xl font-bold">Reset Password</CardTitle>
          <CardDescription>
            Enter your account email and we'll send you a link to choose a new password
          </CardDescription>
        </CardHeader>
        <CardContent>
          {submitted ? (
            <div className="text-center py-6 space-y-4">
              <div className="size-12 rounded-full bg-emerald-100 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400 mx-auto flex items-center justify-center">
                <CheckCircle2 className="size-6" />
              </div>
              <div className="space-y-1">
                <h3 className="font-semibold text-base">Check Your Inbox</h3>
                <p className="text-xs text-muted-foreground max-w-xs mx-auto">
                  If <span className="font-medium text-foreground">{email}</span> is registered, we've sent it a link to choose a new
                  password. The link works once and expires in 30 minutes.
                </p>
              </div>
              <div className="pt-2 flex flex-col gap-2">
                <Link to="/login">
                  <Button variant="default" className="w-full">
                    Back to login
                  </Button>
                </Link>
                <Button
                  variant="ghost"
                  onClick={() => setSubmitted(false)}
                  className="w-full text-xs"
                >
                  Didn't get it? Send again
                </Button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-xs font-semibold">
                  Email
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-2.5 size-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="user@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-9 h-10 text-sm"
                    required
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full h-10 font-semibold"
              >
                {submitting ? (
                  <>
                    <Loader2 className="size-4 animate-spin mr-2" />
                    Sending link...
                  </>
                ) : (
                  "Send reset link"
                )}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
